import { supabase } from '@/lib/supabase'
import { createActivity } from '@/services/activityService'
import type { Tables } from '@/types/database.types'

export type ActivityProposal = Tables<'activity_proposals'>
export type ActivityProposalVote = Tables<'activity_proposal_votes'>

export interface NewActivityProposalInput {
  tripId: string
  title: string
  category?: string
  location?: string
  latitude?: number
  longitude?: number
  startTime?: string
  endTime?: string
  note?: string
}

export interface ActivityProposalWithVotes extends ActivityProposal {
  voteCount: number
  hasVoted: boolean
}

export async function listProposals(tripId: string): Promise<ActivityProposalWithVotes[]> {
  const {
    data: { user },
  } = await supabase.auth.getUser()

  const { data: proposals, error } = await supabase
    .from('activity_proposals')
    .select('*')
    .eq('trip_id', tripId)
    .order('created_at')
  if (error) throw error

  const { data: votes, error: votesError } = await supabase
    .from('activity_proposal_votes')
    .select('proposal_id, user_id')
    .in(
      'proposal_id',
      proposals.map((p) => p.id),
    )
  if (votesError) throw votesError

  return proposals
    .map((proposal) => {
      const proposalVotes = votes.filter((v) => v.proposal_id === proposal.id)
      return {
        ...proposal,
        voteCount: proposalVotes.length,
        hasVoted: !!user && proposalVotes.some((v) => v.user_id === user.id),
      }
    })
    .sort((a, b) => b.voteCount - a.voteCount)
}

export async function createProposal(input: NewActivityProposalInput): Promise<ActivityProposal> {
  const {
    data: { user },
  } = await supabase.auth.getUser()
  if (!user) throw new Error('Not authenticated')

  const { data, error } = await supabase
    .from('activity_proposals')
    .insert({
      trip_id: input.tripId,
      proposed_by: user.id,
      title: input.title,
      category: input.category,
      location: input.location,
      latitude: input.latitude,
      longitude: input.longitude,
      start_time: input.startTime,
      end_time: input.endTime,
      note: input.note,
    })
    .select()
    .single()
  if (error) throw error
  return data
}

export async function castVote(proposalId: string): Promise<void> {
  const {
    data: { user },
  } = await supabase.auth.getUser()
  if (!user) throw new Error('Not authenticated')

  const { error } = await supabase
    .from('activity_proposal_votes')
    .insert({ proposal_id: proposalId, user_id: user.id })
  if (error) throw error
}

export async function retractVote(proposalId: string): Promise<void> {
  const {
    data: { user },
  } = await supabase.auth.getUser()
  if (!user) throw new Error('Not authenticated')

  const { error } = await supabase
    .from('activity_proposal_votes')
    .delete()
    .eq('proposal_id', proposalId)
    .eq('user_id', user.id)
  if (error) throw error
}

export async function acceptProposal(proposal: ActivityProposal): Promise<void> {
  await createActivity({
    tripId: proposal.trip_id,
    title: proposal.title,
    category: proposal.category as 'transport' | 'accommodation' | 'discovery' | 'meal' | undefined,
    location: proposal.location ?? undefined,
    latitude: proposal.latitude ?? undefined,
    longitude: proposal.longitude ?? undefined,
    startTime: proposal.start_time ?? undefined,
    endTime: proposal.end_time ?? undefined,
    note: proposal.note ?? undefined,
  })

  const { error } = await supabase
    .from('activity_proposals')
    .update({ status: 'accepted' })
    .eq('id', proposal.id)
  if (error) throw error
}

export async function rejectProposal(proposalId: string): Promise<void> {
  const { error } = await supabase
    .from('activity_proposals')
    .update({ status: 'rejected' })
    .eq('id', proposalId)
  if (error) throw error
}

export async function deleteProposal(proposalId: string): Promise<void> {
  const { error } = await supabase.from('activity_proposals').delete().eq('id', proposalId)
  if (error) throw error
}
